import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { resolveApiBaseUrl } from '../config/api';
import { initPush, disablePush } from '../native/push';
import ReconnectNotice from '../components/ReconnectNotice';

const AuthContext = createContext(null);

// Native-aware base URL: '/api' on web (same-origin / CRA proxy), absolute
// production origin inside the Capacitor shell (see config/api.js).
axios.defaults.baseURL = resolveApiBaseUrl();

// Only the token-shaped 401s from middleware/auth.js: an expired or invalid
// JWT, or a deactivated account. Exact messages — a "Platform user not
// found" from a staff route must not wipe a member's session. Route-level
// 401s with their own copy fall through. WT-1: shared by the interceptor and
// the boot retry, so the two agree on what "the session is over" means.
const TOKEN_REJECTED = /^(Invalid token|User not found|No token provided)$/i;
const isTokenRejection = (err) =>
  err?.response?.status === 401 && TOKEN_REJECTED.test(String(err?.response?.data?.error || '').trim());

// WT-1 review: did this failed request carry the token we hold NOW? A late
// 401 for an older token (a retry still in flight when someone signed in, or
// the second of several requests that died with an impersonation token) must
// not wipe the session that has replaced it.
const currentToken = () => localStorage.getItem('marakin_platform_token') || localStorage.getItem('marakin_token');
const sentBearer = (config) => {
  const h = config?.headers;
  if (!h) return '';
  return String((typeof h.get === 'function' ? h.get('Authorization') : h.Authorization) || '');
};

// B-72: is this a staff "view as" token (platform.js mints them with
// impersonating: true)? Read from the payload only — the server stays the
// judge of the token itself; this just decides whether a stashed staff token
// may be restored, so a wrong answer can only cost a staffer a sign-in.
const isViewAsToken = (t) => {
  try {
    const part = String(t || '').split('.')[1] || '';
    return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))).impersonating === true;
  } catch { return false; }
};
// B-73: exported for PlatformAuthContext (staff sign-in drops it too).
export const dropViewAsStash = () => {
  try {
    localStorage.removeItem('marakin_impersonation_original_token');
    localStorage.removeItem('marakin_impersonation_return_url');
  } catch { /* storage unavailable */ }
};
const viewAsStash = () => {
  try { return localStorage.getItem('marakin_impersonation_original_token'); } catch { return null; }
};
// B-71: who a view-as session is viewing as, in the shape impersonate() is
// handed by the staff portal (what the Navbar banner and My Clubs read).
const viewAsPerson = (u) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName, email: u.email });

// B-71: the raw token a request was sent with (the staff portal passes the
// one its "view as" request used, which is the staffer's own session).
export const tokenSentWith = (config) => sentBearer(config).replace(/^Bearer\s+/i, '') || null;

// WT-1: waits after each failed boot /auth/me — quick at first (a blip), then
// a steady 15s so a long outage doesn't eat the shared 100-a-minute API
// limit, with ±20% jitter so a clubhouse of phones doesn't retry in step. A
// 429 skips the quick steps: 15s or the server's Retry-After, whichever is
// longer (jittered upwards only). Each attempt gives up after ME_TIMEOUT_MS,
// so a request hanging on one bar of signal counts as a failure; if the first
// is still unanswered after ME_SLOW_MS the notice shows anyway ("checking").
const RESUME_BACKOFF_MS = [2000, 4000, 8000];
const RESUME_STEADY_MS = 15000;
const RESUME_MAX_WAIT_MS = 5 * 60 * 1000; // ceiling on a Retry-After
const ME_TIMEOUT_MS = 15000;
const ME_SLOW_MS = 4000;
const SIGN_OUT_AFTER = 3; // failures before the notice also offers "Sign out"

const jitter = (ms) => Math.round(ms * (0.8 + Math.random() * 0.4));
const retryAfterMs = (res) => { // delta-seconds or an HTTP date
  const h = res?.headers?.['retry-after'];
  if (!h) return 0;
  const secs = Number(h);
  const ms = Number.isFinite(secs) ? secs * 1000 : Date.parse(h) - Date.now();
  return Number.isFinite(ms) && ms > 0 ? Math.min(ms, RESUME_MAX_WAIT_MS) : 0;
};

// WT-1 review: what a failed /auth/me means for a stored session.
//   'expired' token-shaped 401: over (the interceptor clears it, W-10)
//   'offline' no answer, a timeout, or not our API answering (a captive
//             portal's HTML page): keep the token, retry
//   'busy'    408 / 425 / 429 / 5xx: keep the token, retry
//   'refused' any other answer from our API (403, 404, another 4xx): the
//             pre-WT-1 behaviour, clear and stop. Never retried for ever.
const meFailure = (err) => {
  if (isTokenRejection(err)) return 'expired';
  const res = err?.response;
  if (!res) return 'offline';
  const s = res.status;
  if (s === 408 || s === 425 || s === 429 || s >= 500) return 'busy';
  if (!res.data || typeof res.data !== 'object') return 'offline';
  return 'refused';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(null); // { id, firstName, lastName, email }
  // React-query cache must not survive an identity change: on a shared device
  // the next user would be served the PREVIOUS user's cached data until
  // staleTime expired (found 2026-06-10: the consent sheet appeared 5 minutes
  // late after an account switch — exactly the consent query's staleTime).
  const qc = useQueryClient();
  // W-10: set when an authenticated call comes back 401 with a token in hand
  // — i.e. the 7-day JWT expired while the app was closed. Holds the return
  // path; <SessionExpiryRedirect/> (inside the router) turns it into a
  // friendly re-login. Before this, optionalAuthenticate routes kept
  // rendering the hub anonymously while every authed call failed with a raw
  // "Invalid token" (Stuart, 10 Sept, after ten days away).
  const [sessionExpired, setSessionExpired] = useState(null);
  // B-65: set by a password sign-up in this tab, so the Check your inbox
  // screen can greet a new account ("we've just sent…") rather than ask an
  // existing one to confirm. Cleared on any other sign-in or sign-out.
  // WT-10: every brand-new account counts, not just Register: an invite
  // accept that created the account (login(…, { newAccount: true })) and an
  // Apple/Google sign-up (completeSocialSignUp).
  const [justRegistered, setJustRegistered] = useState(false);
  // WT-1: null, or { checking, reason, canSignOut } while a stored session
  // waits on signal (see resumeSession). Drives the global <ReconnectNotice/>.
  const [reconnect, setReconnect] = useState(null);
  const resumeRef = useRef(null); // the running resume: { stopped, busy, failures, step, timer, attempt, … }

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const url = String(err?.config?.url || '');
        const held = currentToken();
        const authRoute = /\/auth\/login|\/platform\/login|\/invite\/accept|\/auth\/register|\/auth\/social/.test(url);
        // WT-1 review: and only for the token we hold now (see sentBearer).
        if (isTokenRejection(err) && held && sentBearer(err?.config) === `Bearer ${held}` && !authRoute) {
          // Impersonation JWTs last 2h: when one dies, hand the staffer their
          // own (still valid) session back instead of logging them out.
          // B-72: only when the token that died WAS a view-as token; a stash
          // next to anyone else's session is a leftover and is dropped.
          const original = isViewAsToken(held) ? localStorage.getItem('marakin_impersonation_original_token') : null;
          if (!original) dropViewAsStash();
          if (original) {
            localStorage.setItem('marakin_token', original);
            localStorage.removeItem('marakin_impersonation_original_token');
            axios.defaults.headers.common['Authorization'] = `Bearer ${original}`;
            setImpersonating(null);
            qc.clear();
            // WT-1: load the staffer through the same retry as boot. This
            // used to setUser(null) on ANY failure, so no signal at that
            // moment left a good staff session looking signed out. No push
            // registration here, as before (review: keep the old behaviour).
            setUser(null);
            setLoading(true);
            resumeSession({ push: false });
            return Promise.reject(err);
          }
          settleResume(); // WT-1 review: a 401 elsewhere mid-retry must not leave `loading` stuck
          localStorage.removeItem('marakin_token');
          localStorage.removeItem('marakin_platform_token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          setImpersonating(null);
          qc.clear();
          setSessionExpired({
            path: `${window.location.pathname}${window.location.search}`,
            // Realm by WHERE they were, not by which key held the token — the
            // staff portal runs on the same users table and shares marakin_token.
            platform: window.location.pathname.startsWith('/marakin-admin'),
          });
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  // eslint-disable-next-line
  }, []);

  // WT-1 (P1, 16 Oct walk-through): load a stored session, and keep trying.
  // Boot used to drop the token on ANY /auth/me failure, so no signal at the
  // ground, a 429 from the shared limit on clubhouse Wi-Fi or a 503 blip
  // signed the person out, and even a reload left them on "Login Required".
  // Now only a token-shaped 401 ends the session — the interceptor above
  // clears it and SessionExpiryRedirect explains — and another answer from
  // our API (403, 404…) clears it as before. No answer, a captive portal, a
  // 429 or a 5xx keeps the token, keeps `loading` true (pages wait instead of
  // bouncing a good session to /login) and retries: on the backoff, at once
  // when the device comes back online or the app/tab is shown again, or on
  // "Try again". After a few failures the notice also offers "Sign out", so
  // nobody is stuck behind it (a shared device, a session that won't load).
  const stopResume = () => {
    const r = resumeRef.current;
    if (!r) return;
    r.stopped = true;
    clearTimeout(r.timer);
    clearTimeout(r.slowTimer);
    window.removeEventListener('online', r.onOnline);
    document.removeEventListener('visibilitychange', r.onVisible);
    resumeRef.current = null;
    setReconnect(null);
  };

  const resumeSession = ({ push = true } = {}) => {
    stopResume();
    const r = { stopped: false, busy: false, failures: 0, step: 0, reason: null, timer: null, slowTimer: null };
    resumeRef.current = r;
    const done = () => { stopResume(); setLoading(false); };
    const show = (checking) => setReconnect({ checking, reason: r.reason, canSignOut: r.failures >= SIGN_OUT_AFTER });
    const retryIn = (ms) => {
      clearTimeout(r.timer);
      // Not while the app/tab is hidden: visibilitychange retries the moment it's back.
      r.timer = setTimeout(() => { if (!document.hidden) attempt(); }, ms);
    };
    const failed = (kind, res) => {
      r.busy = false;
      r.failures += 1;
      r.reason = navigator.onLine === false ? 'offline' : kind;
      if (res?.status === 429) {
        r.step = RESUME_BACKOFF_MS.length; // straight to the steady wait
        retryIn(Math.round(Math.max(RESUME_STEADY_MS, retryAfterMs(res)) * (1 + Math.random() * 0.2)));
      } else {
        retryIn(jitter(RESUME_BACKOFF_MS[r.step] ?? RESUME_STEADY_MS));
        r.step += 1;
      }
      show(false);
    };
    const attempt = () => {
      if (r.stopped || r.busy) return;
      clearTimeout(r.timer);
      // Read it fresh each time: another tab may have signed out (stop) or in.
      const token = currentToken();
      if (!token) {
        delete axios.defaults.headers.common['Authorization']; // WT-1 review: not the old bearer either
        setUser(null);
        done();
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      r.busy = true;
      if (r.failures) show(true);
      else {
        // One bar of signal: the first try can hang for up to ME_TIMEOUT_MS.
        clearTimeout(r.slowTimer);
        r.slowTimer = setTimeout(() => {
          if (r.stopped || !r.busy || r.failures) return;
          r.reason = 'slow';
          show(true);
        }, ME_SLOW_MS);
      }
      axios.get('/auth/me', { timeout: ME_TIMEOUT_MS })
        .then(res => {
          if (r.stopped) return;
          // A 200 that isn't our user (a captive portal's sign-in page): not connected yet.
          if (!res.data || typeof res.data !== 'object' || !res.data.id) { failed('offline'); return; }
          setUser(res.data);
          // B-71: a reload mid view-as kept the session but lost the in-memory
          // `impersonating`, so the "Viewing as" banner and Exit disappeared.
          // Set it again from who loaded whenever this is a view-as session
          // (with no staff token stashed, Exit signs out: exitImpersonation).
          if (isViewAsToken(token)) setImpersonating(viewAsPerson(res.data));
          // B-65 review: a staff "view as" session never registers this device
          // for that person's pushes. B-71 security QA: isViewingAs, so a
          // view-as token with no stash counts too.
          if (push && !isViewingAs(null)) initPush({ userId: res.data.id }); // B-88: unless switched off here
          done();
        })
        .catch(err => {
          if (r.stopped) return; // includes the interceptor ending the session (it settles this)
          const kind = meFailure(err);
          if (kind === 'expired') {
            // The interceptor clears a 401 for the token we hold and stops
            // this; reaching here means the token changed while we asked
            // (another tab): ask again with the new one, or stop if none.
            r.busy = false;
            const now = currentToken();
            if (now && now !== token) attempt();
            else {
              // Not expected (the interceptor clears this case), but never keep a refused token.
              if (now === token) {
                localStorage.removeItem('marakin_token');
                localStorage.removeItem('marakin_platform_token');
                delete axios.defaults.headers.common['Authorization'];
              }
              setUser(null);
              done();
            }
            return;
          }
          if (kind === 'refused') {
            // The server answered and won't have it: as before WT-1.
            localStorage.removeItem('marakin_token');
            localStorage.removeItem('marakin_platform_token');
            delete axios.defaults.headers.common['Authorization'];
            setUser(null);
            done();
            return;
          }
          failed(kind, err?.response);
        });
    };
    r.attempt = attempt;
    r.onOnline = () => { r.step = 0; attempt(); }; // new network: the quick retries again
    r.onVisible = () => { if (!document.hidden) attempt(); };
    window.addEventListener('online', r.onOnline);
    document.addEventListener('visibilitychange', r.onVisible);
    attempt();
  };

  // WT-1: a sign-in or sign-out while a resume is still retrying settles it.
  const settleResume = () => {
    if (!resumeRef.current) return;
    stopResume();
    setLoading(false);
  };

  useEffect(() => {
    // B-72: a "view as" stash only belongs next to a view-as session. Browsers
    // that logged out mid view-as before this fix still hold one: drop it.
    if (!isViewAsToken(currentToken())) dropViewAsStash();
    // Check for platform token first, then regular token
    if (currentToken()) resumeSession();
    else setLoading(false);
    return stopResume; // timers + listeners
  // eslint-disable-next-line
  }, []);

  // B-73 security QA: the "re-sync when another tab signs in to the staff
  // portal" storage listener that lived here is gone. It only acted on the
  // pre-v15 marakin_platform_token key, which nothing has written since v15
  // (staff sign-ins use marakin_token), so it could never fire. Reading the
  // current token instead would swap a member's tab onto whoever signed in
  // in another tab, on any storage change.

  // WT-1 security QA (17 Oct): a "view as" stash or a saved return path must
  // never outlive the session that made it. Log out during a view-as used to
  // leave the staffer's own token stashed; the next person on that browser
  // whose session was later refused was swapped onto it (the interceptor's
  // impersonation restore). Cleared on every sign-in and sign-out.
  const forgetSessionLeftovers = () => {
    try {
      localStorage.removeItem('marakin_impersonation_original_token');
      localStorage.removeItem('marakin_impersonation_return_url');
      localStorage.removeItem('marakin_return_after_confirm'); // WT-5 return path (lib/returnPath.js)
    } catch { /* storage unavailable */ }
  };

  // WT-10: `newAccount` when the account was created a moment ago (the invite
  // page creates it, then signs in here), so Check your inbox greets it as new.
  const login = async (email, password, { newAccount = false } = {}) => {
    const res = await axios.post('/auth/login', { email, password });
    const { token } = res.data;
    settleResume(); // WT-1: signed in from the login page while a resume was retrying
    forgetSessionLeftovers();
    setImpersonating(null); // B-73: no stale "Viewing as" banner after signing in from inside a view-as
    setJustRegistered(!!newAccount);
    qc.clear(); // new identity — drop every cached query from the previous one
    localStorage.setItem('marakin_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Fetch full profile with memberships
    const meRes = await axios.get('/auth/me');
    setUser(meRes.data);
    initPush({ userId: meRes.data?.id }); // request permission + register this device (native only; B-88: unless switched off here)
    return meRes.data;
  };

  const register = async (data) => {
    const res = await axios.post('/auth/register', data);
    const { token, user } = res.data;
    settleResume(); // WT-1
    forgetSessionLeftovers();
    setImpersonating(null); // B-73
    setJustRegistered(true);
    qc.clear(); // new identity — drop every cached query
    localStorage.setItem('marakin_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // B-57: register may have accepted a waiting invite, so load the full
    // profile with memberships (as login does). Fall back to the bare user.
    let full = user;
    try { full = (await axios.get('/auth/me')).data; } catch { /* keep the bare user */ }
    setUser(full);
    initPush({ userId: full?.id }); // request permission + register this device (native only; B-88: unless switched off here)
    return full;
  };

  // B-53: Continue with Apple / Google. The server verifies the provider's
  // ID token and answers either with the same session body as /auth/login
  // (signed in, exactly as login() leaves things) or, for someone new, with
  // { needsProfile, signupTicket, email, firstName, lastName } — the caller
  // then shows the DOB + terms step and calls completeSocialSignUp.
  // WT-10: `newAccount` for an Apple/Google sign-up (completeSocialSignUp).
  const adoptSession = async (token, fallbackUser, { newAccount = false } = {}) => {
    settleResume(); // WT-1
    forgetSessionLeftovers();
    setImpersonating(null); // B-73
    setJustRegistered(!!newAccount);
    qc.clear(); // new identity — drop every cached query
    localStorage.setItem('marakin_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    let full = fallbackUser;
    try { full = (await axios.get('/auth/me')).data; } catch { /* keep the bare user */ }
    setUser(full);
    initPush({ userId: full?.id }); // request permission + register this device (native only; B-88: unless switched off here)
    return full;
  };

  const socialSignIn = async (credential) => {
    const res = await axios.post('/auth/social', credential);
    // needsEmailCode (security review): a Google address Google doesn't host
    // matched an existing account — the emailed code must be entered first.
    if (res.data?.needsProfile || res.data?.needsEmailCode) return { ...res.data };
    // Someone else may be signed in on this device: adoptSession replaces
    // the token and clears the cache, as login() does.
    return { user: await adoptSession(res.data.token, res.data.user) };
  };

  const completeSocialSignUp = async (data) => {
    const res = await axios.post('/auth/social/complete', data);
    // WT-10: a brand-new account (only an unproven Google address is held at
    // Check your inbox; Apple and authoritative Google arrive confirmed).
    return adoptSession(res.data.token, res.data.user, { newAccount: true });
  };

  const confirmSocialLink = async ({ linkTicket, code }) => {
    const res = await axios.post('/auth/social/link-code', { linkTicket, code });
    return adoptSession(res.data.token, res.data.user);
  };

  // B-65 addition: Change password / Set a password (My account). The server
  // bumps session_epoch, which signs every OTHER device out, and replies with
  // a fresh token for this one. Same identity, so the query cache stays.
  // Same identity, new session token (the server bumped session_epoch, so
  // the old token is now refused): Change password and Change email.
  const replaceToken = async (token) => {
    localStorage.setItem('marakin_token', token);
    localStorage.removeItem('marakin_platform_token');
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    const fresh = await refreshUser();
    // the server dropped every push registration; this device re-registers
    // (native only; B-88: unless the person switched it off here)
    initPush({ userId: fresh?.id || user?.id });
  };

  const changePassword = async ({ currentPassword, newPassword }) => {
    const res = await axios.post('/auth/change-password', { currentPassword, newPassword });
    await replaceToken(res.data.token);
    return res.data;
  };

  // B-65 security review: needs the current password; signs other devices out.
  const changeEmail = async ({ email, currentPassword }) => {
    const res = await axios.post('/auth/change-email', { email, currentPassword });
    await replaceToken(res.data.token);
    return res.data;
  };

  const logout = () => {
    settleResume(); // WT-1: nothing left to resume
    // best-effort: drop this device's push token first (native). Not on a
    // "view as" session: it never registered one (and the server refuses).
    if (!isViewingAs(null)) disablePush(); // B-71 security QA: stash or view-as token
    localStorage.removeItem('marakin_token');
    localStorage.removeItem('marakin_platform_token');
    forgetSessionLeftovers(); // WT-1 security QA: never hand the next person a stashed staff token
    // Guided add-child suggestions carry children's names — same shared-device
    // privacy rule as the query cache below.
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('marakin_pending_children:'))
        .forEach(k => localStorage.removeItem(k));
    } catch { /* storage unavailable */ }
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setImpersonating(null); // B-73: no "Viewing as" banner left over for the next sign-in
    setJustRegistered(false);
    qc.clear(); // privacy: no cached data may outlive the session on a shared device
  };

  // Re-fetch /auth/me and update user in place. Used after actions that
  // change memberships (self-serve club creation) or membership fields
  // (guided-tour completion) without a full login round-trip.
  const refreshUser = async () => {
    try {
      const res = await axios.get('/auth/me');
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  const getMembership = (clubSlug) => {
    if (!user?.memberships) return null;
    return user.memberships.find(m => m.clubSlug === clubSlug);
  };

  // Called by platform admin to assume a user's persona. `staffToken` is the
  // token the staff portal's "view as" request was sent with (the staffer's
  // own session; PlatformClubs passes it).
  const impersonate = async (token, userInfo, { staffToken } = {}) => {
    // Stash the staffer's own token so Exit can restore it.
    // B-71: a second "view as" (browser Back to the staff portal, View as
    // again) used to stash the FIRST view-as token as the "original", so Exit
    // landed on the first member and the staffer's own sign-in was lost. A
    // view-as token is never stashed. The staff portal's own token comes
    // first (security QA: another tab may have left a member's token in
    // storage); without one, mid view-as the existing stash is kept, else
    // whatever is signed in now.
    const notViewAs = (t) => (t && !isViewAsToken(t) ? t : null);
    const current = currentToken();
    const originalToken = notViewAs(staffToken)
      || (isViewAsToken(current) ? notViewAs(viewAsStash()) : current);
    qc.clear(); // identity change — drop cached queries
    if (originalToken) localStorage.setItem('marakin_impersonation_original_token', originalToken);
    else localStorage.removeItem('marakin_impersonation_original_token'); // never a view-as token
    localStorage.setItem('marakin_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    try {
      const meRes = await axios.get('/auth/me');
      setUser(meRes.data);
    } catch (err) {
      // fallback — use the userInfo passed in
    }
    setImpersonating(userInfo);
  };

  // Exit impersonation and restore original platform session
  const exitImpersonation = async () => {
    const originalToken = localStorage.getItem('marakin_impersonation_original_token');
    qc.clear(); // identity change — drop cached queries
    if (originalToken) {
      // Restore into the UNIFIED post-v15 key. (Landed 24 Aug as the B-3 fix;
      // it is correct and worth keeping, but it was NOT the root cause —
      // Exit still dumped you at the admin login screen on 26 Aug. The real
      // bug was in PlatformAuthContext.applyToken, which set the bearer on
      // platformAxios only while the boot effect called /auth/me on the
      // global axios; see the comment there. Nothing here was ever a race.)
      localStorage.setItem('marakin_token', originalToken);
      localStorage.removeItem('marakin_platform_token');
      localStorage.removeItem('marakin_impersonation_original_token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`;
      try {
        const meRes = await axios.get('/auth/me');
        setUser(meRes.data);
      } catch (err) {
        setUser(null);
      }
      setImpersonating(null);
      return true;
    }
    // B-71 security QA: a view-as session with no staff token to go back to
    // (the banner shows for any view-as token now): Exit signs out rather
    // than leave the member's session behind. The caller sends the staffer
    // to the staff sign-in.
    if (isViewAsToken(currentToken())) logout();
    setImpersonating(null);
    return false;
  };

  // Called after platform login to sync the club-side auth. `fallbackUser` is
  // the sign-in body's user (no memberships: those arrive with the next
  // /auth/me, e.g. a reload).
  // B-73: a staff sign-in is a sign-in here too, exactly as adoptSession: it
  // ends any view-as this tab still shows a banner for, and the leftovers.
  // It used to read only the pre-v15 key, which the staff sign-in had just
  // removed, so the club side kept showing whoever was signed in before (the
  // person being viewed as, banner gone). Security QA: and if /auth/me fails
  // (a 429 or 503) the screen falls back to the staffer, never the previous
  // identity.
  const syncPlatformUser = async (fallbackUser) => {
    settleResume();
    forgetSessionLeftovers();
    setImpersonating(null);
    setJustRegistered(false);
    qc.clear(); // new identity — drop every cached query from the previous one
    const token = currentToken();
    if (!token) { setUser(null); return; }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    let full = fallbackUser || null;
    try { full = (await axios.get('/auth/me')).data; } catch { /* never the previous identity */ }
    setUser(full);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, socialSignIn, completeSocialSignUp, confirmSocialLink, logout, getMembership, refreshUser, syncPlatformUser, impersonate, exitImpersonation, impersonating, sessionExpired, clearSessionExpired: () => setSessionExpired(null), justRegistered, changePassword, changeEmail }}>
      {children}
      {/* WT-1: "Can't reach Marakin" while a stored session waits on signal.
          Out here, not in the router: AuthProvider owns the retry. */}
      <ReconnectNotice state={reconnect} onRetry={() => resumeRef.current?.attempt()} onSignOut={logout} />
    </AuthContext.Provider>
  );
};

/**
 * SessionExpiryRedirect — mount once inside <BrowserRouter/>. AuthProvider
 * sits outside the router (it can't navigate), so this bridges the W-10
 * interceptor's flag into a redirect: to the login page for the right realm,
 * carrying `from` (so login returns them to where they were) and `expired`
 * (so the login page can say why).
 */
export const SessionExpiryRedirect = () => {
  const { sessionExpired, clearSessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (!sessionExpired) return;
    const dest = sessionExpired.platform ? '/marakin-admin/login' : '/login';
    const from = sessionExpired.path && !sessionExpired.path.includes('/login') ? sessionExpired.path : undefined;
    if (location.pathname === dest) { clearSessionExpired(); return; } // already there (e.g. wrong password after expiry)
    // Next tick, not this commit: the page that took the 401 usually reacts
    // to the now-null user in the same commit (AdminPage's <Navigate> to the
    // hub, for one) and, being later in the tree, would win. We must be last.
    // The flag is cleared inside the timer — clearing it first would re-run
    // this effect and cancel the very navigation we're waiting to make.
    const t = setTimeout(() => {
      navigate(dest, { replace: true, state: { from, expired: true } });
      clearSessionExpired();
    }, 0);
    return () => clearTimeout(t);
  // eslint-disable-next-line
  }, [sessionExpired]);
  return null;
};

/**
 * B-65: should the app hold this person at "Check your inbox"? Signed in,
 * email not confirmed — except Marakin staff (made by staff; exempt) and a
 * staffer viewing as someone (support needs to see their app). Children
 * never sign in. `=== false`, so an older server that doesn't send the field
 * never gates anyone. The SERVER does not enforce this (frozen API: 1.2 store
 * builds can't show the screen), so it is a product gate, not a security one.
 */
/**
 * B-65 review: is this a staff "view as" session? `impersonating` is only set
 * in memory by impersonate(); after a reload the session survives through the
 * stashed original token, so check that too. Account-security actions
 * (password, email, consents, GDPR requests, invite / co-parent accepts, push
 * registration) are refused by the server on such a session; screens hide them.
 */
// B-71: or the token held is itself a view-as token (even with no stash).
export const isViewingAs = (impersonating) => {
  if (impersonating) return true;
  try {
    return !!localStorage.getItem('marakin_impersonation_original_token') || isViewAsToken(currentToken());
  } catch { return false; }
};
export const VIEWING_AS_NOTE = 'Not available while viewing as this user.';

export const needsEmailConfirm = (user, impersonating) =>
  !!user && user.emailVerified === false && !user.platformRole && !user.isPlatformUser && !isViewingAs(impersonating);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
